import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Briefcase, FileText, Calendar } from "lucide-react";
import { DailyLogComposer, DailyLogTimeline } from "@/components/DailyLog";

/**
 * Employee-side "Daily field log" hub.
 *
 * Pick a project (from your active work orders), post today's update,
 * scroll the recent timeline for that job.
 */
export default function EmployeeDailyLog() {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/work-orders")
      .then((r) => setWorkOrders(r.data || []))
      .catch((err) => console.warn("WOs fetch:", err))
      .finally(() => setLoading(false));
    api.get("/me/daily-logs", { params: { limit: 10 } })
      .then((r) => setRecent(r.data || []))
      .catch((err) => console.warn("My logs fetch:", err));
  }, []);

  // Unique projects from this employee's work orders (admin sees all WOs).
  const projects = useMemo(() => {
    const map = new Map();
    workOrders.forEach((w) => {
      if (w.project_id && !map.has(w.project_id)) {
        map.set(w.project_id, {
          project_id: w.project_id,
          title: w.project_title || w.title || w.customer_code || w.project_id,
          customer_code: w.customer_code,
        });
      }
    });
    return Array.from(map.values());
  }, [workOrders]);

  useEffect(() => {
    if (!selectedProject && projects.length > 0) setSelectedProject(projects[0].project_id);
  }, [projects, selectedProject]);

  return (
    <div data-testid="employee-daily-log">
      <p className="overline">Field operations</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Daily Field Log</h1>
      <p className="text-sm text-[#475569] dark:text-slate-400 mt-1 mb-6">
        Post today's progress + photos. Clients see this on their project page in real time.
      </p>

      {loading ? (
        <div className="card-pb p-6 text-sm text-[#475569]">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="card-pb p-8 text-center">
          <Briefcase className="w-10 h-10 text-[#E2E8F0] mx-auto mb-2" />
          <p className="text-sm text-[#475569]">No active work orders yet — ask admin to assign one.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[280px_1fr] gap-5">
          {/* Project list */}
          <aside className="space-y-2">
            <div className="overline mb-2">My active projects</div>
            {projects.map((p) => (
              <button
                key={p.project_id}
                onClick={() => setSelectedProject(p.project_id)}
                data-testid={`pick-project-${p.project_id}`}
                className={`w-full text-left rounded-lg border px-3 py-2.5 transition ${
                  selectedProject === p.project_id
                    ? "border-[#C9A356] bg-[#FFFBF0] dark:bg-[#3a2e16]/40 shadow-sm"
                    : "border-[#E2E8F0] dark:border-slate-700 hover:border-[#C9A356] bg-white dark:bg-slate-900"
                }`}
              >
                <div className="text-sm font-medium text-[#0A2540] dark:text-white truncate">{p.title}</div>
                {p.customer_code && <div className="text-[11px] font-mono text-[#94A3B8] mt-0.5">{p.customer_code}</div>}
              </button>
            ))}

            {recent.length > 0 && (
              <div className="mt-6">
                <div className="overline mb-2">Recent posts</div>
                <ul className="text-xs text-[#475569] dark:text-slate-400 space-y-1.5">
                  {recent.slice(0, 5).map((r) => (
                    <li key={r.log_id} className="flex items-start gap-1.5">
                      <FileText className="w-3 h-3 mt-0.5 shrink-0 text-[#C9A356]" />
                      <span className="truncate">{r.summary}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>

          <div className="space-y-5">
            {selectedProject && (
              <>
                <DailyLogComposer projectId={selectedProject} onSaved={() => setRefreshKey((k) => k + 1)} />
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-[#C9A356]" />
                    <span className="overline">Recent on this project</span>
                  </div>
                  <DailyLogTimeline projectId={selectedProject} refreshKey={refreshKey} />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
